import { filterClasses } from "@swivl-mono/helpers";
import { useEffect, useState } from "react";

const CONCURRENCY_STORAGE_KEY = "swivl.eval.concurrency";

/**
 * Read the persisted "case-group concurrency" preference. Mirrors the
 * AttemptsSetting pattern — localStorage-backed, shared across every surface
 * that starts an eval run. Defaults to 1 (sequential / latency-accurate).
 *
 * concurrency > 1 inflates `latencyMs` numbers; for latency benchmarks keep
 * this at 1. Action cases (those whose criteria target fixture-mutating tools
 * like capture_lead, manage_account) auto-sequentialize regardless of this
 * value to avoid races on the shared fixture user/location.
 */
export function readConcurrency(): number {
  if (typeof window === "undefined") return 1;
  const raw = window.localStorage.getItem(CONCURRENCY_STORAGE_KEY);
  if (!raw) return 1;
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(8, n);
}

export function writeConcurrency(n: number): void {
  if (typeof window === "undefined") return;
  const clamped = Math.max(1, Math.min(8, Math.floor(n)));
  window.localStorage.setItem(CONCURRENCY_STORAGE_KEY, String(clamped));
  window.dispatchEvent(new CustomEvent("swivl:eval-concurrency-changed", { detail: clamped }));
}

interface ConcurrencySettingProps {
  variant?: "compact" | "inline";
  hideHelp?: boolean;
  onChange?: (n: number) => void;
}

/**
 * Editable "concurrency" widget. 1 = sequential (legacy + latency-accurate).
 * 4-8 = parallel case-groups for fast iteration on large runs. Persists to
 * localStorage; cross-mount sync via custom `swivl:eval-concurrency-changed`
 * event.
 */
export function ConcurrencySetting({ variant = "compact", hideHelp = false, onChange }: ConcurrencySettingProps): React.ReactElement {
  const [value, setValue] = useState<number>(() => readConcurrency());

  useEffect(() => {
    const handler = (e: Event): void => {
      const detail = (e as CustomEvent<number>).detail;
      if (typeof detail === "number") setValue(detail);
    };
    window.addEventListener("swivl:eval-concurrency-changed", handler);
    return () => window.removeEventListener("swivl:eval-concurrency-changed", handler);
  }, []);

  const set = (n: number): void => {
    const clamped = Math.max(1, Math.min(8, Math.floor(n)));
    setValue(clamped);
    writeConcurrency(clamped);
    onChange?.(clamped);
  };

  return (
    <div className={filterClasses("flex flex-col gap-1 text-sm", variant === "compact" ? "" : "")}>
      <div className="flex items-center gap-2">
        <label htmlFor="eval-concurrency" className="text-grey-500">
          Concurrency
        </label>
        <input
          id="eval-concurrency"
          type="number"
          min={1}
          max={8}
          value={value}
          onChange={(e) => set(parseInt(e.target.value || "1", 10))}
          className="w-14 rounded border border-grey-200 px-2 py-1 text-center"
          title="How many case-groups run in parallel. 1 = sequential (latency-accurate). 4-8 = fast iteration. Action cases (capture_lead, manage_account, etc.) auto-sequentialize."
        />
        <div className="flex gap-0.5">
          {[1, 4, 8].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => set(n)}
              className={filterClasses(
                "rounded px-1.5 py-0.5 text-xs",
                value === n ? "bg-orange-500 text-white" : "bg-grey-100 text-grey-600 hover:bg-grey-200"
              )}
            >
              {n}
            </button>
          ))}
        </div>
      </div>
      {!hideHelp && (
        <p className="text-[11px] text-grey-400">
          Case-groups that run in parallel. 1 = sequential (latency-accurate). Higher = faster but inflates latency numbers. Action cases
          auto-sequentialize.
        </p>
      )}
    </div>
  );
}
